import React, { useEffect, useState, useRef } from 'react';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import './MarketIndicesWidget.css';
import { getFlagSvg } from '../../Components/Common/Flag/Flag';
import { MarketIndicesType, StockData } from '../../types/indices.type';
import classNames from 'classnames';
import { formatNumber } from '../../utils/utils';
import { CUST_EMPTYFIELD_TEXT } from '../../utils/constant';
import { getMarketIndices } from '../../api/quote.api';
import SvgImage from '../../Components/Common/SvgImage/SvgImage';
import {
  Region,
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';

import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';

import { apiBase } from '../../api/apiBase';

const LINK_ENABLED_RICS = [
  '.AXJO',
  '.ATLI',
  '.AFLI',
  '.ATOI',
  '.AORD',
  '.DJI',
  '.IXIC',
  '.SPX',
];

// Decimal places for the index table, mapped per symbol.
// Reference: Figma "Bell FG – Master" node 17375-204244. Symbols not listed
// fall back to 2 decimal places. `last` and `nc` can differ (e.g. SLc1/CLc1).
const INDEX_LAST_DECIMAL_PLACES: Record<string, number> = {
  IXIC: 3,
  US10YT: 3,
  US30YT: 3,
  NGc1: 3,
  HGc1: 4,
};

const INDEX_CHANGE_DECIMAL_PLACES: Record<string, number> = {
  IXIC: 3,
  US10YT: 3,
  US30YT: 3,
  NGc1: 3,
  SLc1: 3,
  CLc1: 3,
  HGc1: 4,
};

const getIndexLastDecimalPlaces = (symbol?: string | null): number =>
  symbol != null ? INDEX_LAST_DECIMAL_PLACES[symbol] ?? 2 : 2;

const getIndexChangeDecimalPlaces = (symbol?: string | null): number =>
  symbol != null ? INDEX_CHANGE_DECIMAL_PLACES[symbol] ?? 2 : 2;

export interface MarketIndicesWidgetProps extends BaseWidgetProps {
  region?: Region;
  token?: string;
  onCallback?: (message: StockData) => void;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const MarketIndicesWidget = (props: MarketIndicesWidgetProps) => {
  const { onHeightChange } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(
    containerRef,
    WidgetNameSource.MarketIndices,
    onHeightChange,
  );

  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'indexList');
  const [indices, setIndices] = useState<MarketIndicesType[]>([]);

  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });

  useEffect(() => {
    const handleScroll = () => {
      setTooltip((prev: any) => ({ ...prev, show: false }));
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const constrainTooltipPosition = (e: React.MouseEvent) => {
    let x = e.pageX + 10;
    let y = e.pageY;

    const elementMaxWidth = tooltipRef.current?.clientWidth || 0;
    const elementMaxHeight = tooltipRef.current?.clientHeight || 0;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (x + elementMaxWidth > viewportWidth) {
      x = x - elementMaxWidth;
    }
    if (y + elementMaxHeight > viewportHeight) {
      y = y - elementMaxHeight;
    }

    return { x, y };
  };

  function renderNameTooltip(item: MarketIndicesType, isDesktop: boolean) {
    if (!isDesktop) {
      const handleClickName = (e: React.MouseEvent<HTMLSpanElement>) => {
        const target = e.currentTarget;
        const rect = target.getBoundingClientRect();
        const scrollX =
          window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY =
          window.pageYOffset || document.documentElement.scrollTop;

        const x = rect.left + scrollX;
        const y = rect.bottom + 2 + scrollY;

        setTooltip({
          content: item.name,
          show: true,
          x,
          y,
        });
      };
      return (
        <span className={classNames('name')} onClick={handleClickName}>
          {item.name}
        </span>
      );
    }

    return (
      <span
        className={classNames('name')}
        onMouseEnter={(e) => {
          const constrainedPosition = constrainTooltipPosition(e);
          setTooltip({
            show: true,
            x: constrainedPosition.x,
            y: constrainedPosition.y,
            content: item.name,
          });
        }}
        onMouseMove={(e) => {
          if (e.movementX === 0 && e.movementY === 0) return;
          const constrainedPosition = constrainTooltipPosition(e);
          setTooltip((prev) => ({
            ...prev,
            x: constrainedPosition.x,
            y: constrainedPosition.y,
          }));
        }}
        onMouseLeave={() => setTooltip((prev) => ({ ...prev, show: false }))}
        onMouseOut={() => setTooltip((prev) => ({ ...prev, show: false }))}
      >
        {item.name}
      </span>
    );
  }

  useEffect(() => {
    setIsLoading(true);
    setIsEmptyData(false);
    getMarketIndices(props.region || Region.AU, props.token || '')
      .then((res) => {
        const arr: MarketIndicesType[] = [];
        for (const key in res) {
          if (res[key]) {
            let haveImg = true;
            if (key.endsWith('=RR') || key.endsWith('=XX')) {
              res[key].cc = 'USA';
              haveImg = false;
            }
            if (key === '.FTSE') {
              res[key].cc = 'GBP';
            }
            arr.push({
              ric: key,
              index: res[key].symbol,
              cc: res[key].cc,
              name: res[key].name,
              last: res[key].last,
              pc: res[key].pctchng,
              nc: res[key].netchng,
              haveImg: haveImg,
            });
          }
        }
        setIndices(arr);
        setIsEmptyData(arr.length === 0);
        if (arr.length === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'MarketIndicesWidget',
            data: null,
          });
        }
      })
      .catch((err) => {
        console.log(err);
        props.onError?.({
          message: err.message,
          type: err.name + '_' + err.code,
        });
        setIsEmptyData(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [props.region]);

  const getIndexChart = (ric: string) => {
    return `${apiBase()}/chart/?int=2&sym=${ric}&style=1&per=1&token=${encodeURIComponent(props.token || '')}`;
  };

  const handleOnClick = (symbol: string) => {
    if (props.onCallback && symbol) {
      let message = {
        data: { symbol: symbol },
        source: 'marketindices',
        type: WidgetMessageType.OnStockClick,
      };
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify(message),
          '*',
        );
      }
      window.parent.postMessage(JSON.stringify(message), '*');
      props.onCallback(message);
    }
  };

  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div ref={containerRef} className="marketIndices">
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th className="mobile-only-header">Ctry / Stock Index</th>

                <th className="desktop-only ctry-index-header" colSpan={2}>
                  Ctry / Stock Index
                </th>

                <th className="desktop-only name-header"></th>

                <th className="chart-header">1 Day</th>

                <th className="mobile-only-header text-right">Last / Chg</th>

                <th className="desktop-only text-right">Last Price</th>
                <th className="desktop-only text-right">Chg</th>
                <th className="desktop-only text-right pct-cell">%Chg</th>
              </tr>
            </thead>

            <div className="market-indices-table-spacer"></div>

            <tbody>
              {indices
                ? indices.map((item: MarketIndicesType) => (
                    <tr key={item.ric}>
                      <td className="mobile-only-cell">
                        <div className="info">
                          <div className="flag-icon">{getFlagSvg(item.cc)}</div>
                          <div className="table-flex-column">
                            <span
                              className={classNames('index', {
                                'link-enabled': LINK_ENABLED_RICS.includes(
                                  item.ric,
                                ),
                              })}
                              onClick={
                                LINK_ENABLED_RICS.includes(item.ric)
                                  ? () => handleOnClick(item.ric)
                                  : undefined
                              }
                            >
                              {item.index}
                            </span>
                            {renderNameTooltip(item, false)}
                          </div>
                        </div>
                      </td>
                      <td className="desktop-only ctry-cell">
                        <div className="flag-icon">{getFlagSvg(item.cc)}</div>
                      </td>
                      <td className="desktop-only index-cell">
                        <span
                          className={classNames('index', {
                            'link-enabled': LINK_ENABLED_RICS.includes(
                              item.ric,
                            ),
                          })}
                          onClick={
                            LINK_ENABLED_RICS.includes(item.ric)
                              ? () => handleOnClick(item.ric)
                              : undefined
                          }
                        >
                          {item.index}
                        </span>
                      </td>
                      <td className="desktop-only name-cell">
                        {renderNameTooltip(item, true)}
                      </td>

                      <td className="chart-cell">
                        {item.haveImg ? (
                          <SvgImage svgApiUrl={getIndexChart(item.ric)} />
                        ) : null}
                      </td>

                      <td className="mobile-only-cell">
                        <div className="price">
                          <div className="last">
                            {formatNumber(
                              item.last,
                              getIndexLastDecimalPlaces(item.index),
                              getIndexLastDecimalPlaces(item.index),
                              false,
                              false,
                              false,
                            )}
                          </div>
                          <div className="chg">
                            <span
                              className={classNames('nc', {
                                up: Number(item.nc) > 0,
                                down: Number(item.nc) < 0,
                              })}
                            >
                              {formatNumber(
                                item.nc,
                                getIndexChangeDecimalPlaces(item.index),
                                getIndexChangeDecimalPlaces(item.index),
                                false,
                                true,
                                false,
                              )}
                            </span>
                            <span
                              className={classNames('pc', {
                                up: Number(item.pc) > 0,
                                down: Number(item.pc) < 0,
                              })}
                            >
                              {formatNumber(
                                item.pc,
                                2,
                                2,
                                false,
                                true,
                                false,
                                '%',
                              )}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td className="desktop-only text-right last-cell">
                        <span className="last">
                          {formatNumber(
                            item.last,
                            getIndexLastDecimalPlaces(item.index),
                            getIndexLastDecimalPlaces(item.index),
                            false,
                            false,
                            false,
                          )}
                        </span>
                      </td>
                      <td className="desktop-only text-right chg-cell">
                        <span
                          className={classNames('nc', {
                            up: Number(item.nc) > 0,
                            down: Number(item.nc) < 0,
                          })}
                        >
                          {formatNumber(
                            item.nc,
                            getIndexChangeDecimalPlaces(item.index),
                            getIndexChangeDecimalPlaces(item.index),
                            false,
                            true,
                            false,
                          )}
                        </span>
                      </td>
                      <td className="desktop-only text-right pct-cell">
                        <span
                          className={classNames('pc', {
                            up: Number(item.pc) > 0,
                            down: Number(item.pc) < 0,
                          })}
                        >
                          {formatNumber(item.pc, 2, 2, false, true, false, '%')}
                        </span>
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        )}

        <CommonTooltip
          ref={tooltipRef}
          content={tooltip.content}
          show={tooltip.show}
          x={tooltip.x}
          y={tooltip.y}
        />
      </div>
    </Layout>
  );
};

export default MarketIndicesWidget;
